import * as React from 'react';
import { createAppSettingsRouteWithSettingType } from '../../helpers/routeHelpers';
import { AppPlatform, ReleaseStepType, SettingType } from '@/types';
import {
    SettingOutlined,
    GoldOutlined,
    CompassOutlined,
    AppstoreOutlined,
    MobileOutlined,
    HeartOutlined,
    ThunderboltOutlined,
    NotificationOutlined,
    CalendarOutlined,
    ControlOutlined,
    CheckSquareOutlined,
    FileTextOutlined,
    RocketOutlined,
    TeamOutlined,
    ToolOutlined,
    InboxOutlined,
} from '@ant-design/icons';

export interface MenuItem {
    id: string;
    label: string;
    path: string;
    icon: () => JSX.Element;
    showOnlyOnPlatforms?: AppPlatform[];
    subroute?: (orgId: string, appId: string) => string;
    isRunwayAdminRouteOnly?: boolean;
    minimumRequiredMigratedWorkflowStepsToShow?: ReleaseStepType[];
    minimumRequiredMigratedWorkflowStepsToHide?: ReleaseStepType[];
    showOnlyForWhiteLabelApps?: boolean;
    hideForTriageExcludedOrgs?: boolean;
}

export const settingsMenuItems: MenuItem[] = [
    {
        id: SettingType.General,
        label: 'General',
        path: `/${SettingType.General}`,
        icon: () => <SettingOutlined />,
    },
    {
        id: SettingType.WorkflowManagement,
        label: 'Flightpath',
        path: `/${SettingType.WorkflowManagement}`,
        icon: () => <GoldOutlined />,
        minimumRequiredMigratedWorkflowStepsToShow: [ReleaseStepType.BetaTesting],
    },
    {
        id: SettingType.Team,
        label: 'Team',
        path: `/${SettingType.Team}`,
        icon: () => <TeamOutlined />,
    },
    {
        id: SettingType.ReleasePilotRotation,
        label: 'Release pilot rotation',
        path: `/${SettingType.ReleasePilotRotation}`,
        icon: () => <CompassOutlined />,
    },
    {
        id: SettingType.Integrations,
        label: 'Integrations',
        path: `/${SettingType.Integrations}`,
        icon: () => <AppstoreOutlined />,
    },
    {
        id: SettingType.ProfilesAndDevices,
        label: 'Profiles and devices',
        path: `/${SettingType.ProfilesAndDevices}`,
        icon: () => <MobileOutlined />,
        showOnlyOnPlatforms: [AppPlatform.Ios],
    },
    {
        id: SettingType.SigningKeys,
        label: 'Signing keys',
        path: `/${SettingType.SigningKeys}`,
        icon: () => <MobileOutlined />,
        showOnlyOnPlatforms: [AppPlatform.Android],
    },
    {
        id: SettingType.HealthMetrics,
        label: 'Health metrics',
        path: `/${SettingType.HealthMetrics}`,
        icon: () => <HeartOutlined />,
    },
    {
        id: SettingType.Automations,
        label: 'Automations',
        path: `/${SettingType.Automations}`,
        icon: () => <ThunderboltOutlined />,
    },
    {
        id: SettingType.Notifications,
        label: 'Notifications',
        path: `/${SettingType.Notifications}`,
        icon: () => <NotificationOutlined />,
    },
    {
        id: SettingType.Schedule,
        label: 'Schedule',
        path: `/${SettingType.Schedule}`,
        icon: () => <CalendarOutlined />,
        subroute: (orgId, appId) => createAppSettingsRouteWithSettingType(orgId, appId, SettingType.ScheduleCadence),
    },
    {
        id: 'app-store',
        label: 'App store defaults',
        path: `/${SettingType.AppStore}`,
        icon: () => <ControlOutlined />,
        showOnlyOnPlatforms: [
            AppPlatform.Android,
            AppPlatform.Ios,
            AppPlatform.VisionOS,
            AppPlatform.TvOS,
            AppPlatform.WearOS,
        ],
        subroute: (orgId, appId) =>
            createAppSettingsRouteWithSettingType(orgId, appId, SettingType.DefaultReleaseNotes),
        showOnlyForWhiteLabelApps: true,
    },
    {
        id: SettingType.Checklists,
        label: 'Checklists',
        path: `/${SettingType.Checklists}`,
        icon: () => <CheckSquareOutlined />,
    },
    {
        id: 'custom-strings',
        label: 'Custom strings',
        path: `/${SettingType.CustomStrings}`,
        icon: () => <FileTextOutlined />,
    },
    {
        id: 'fix-requests',
        label: 'Fixes',
        path: `/${SettingType.FixRequests}`,
        icon: () => <ToolOutlined />,
    },
    {
        id: SettingType.Triage,
        label: 'Triage',
        path: `/${SettingType.Triage}`,
        icon: () => <InboxOutlined />,
        hideForTriageExcludedOrgs: true,
    },
    {
        id: 'setup-tasks',
        label: 'Setup tasks',
        path: `/${SettingType.SetupTasks}`,
        icon: () => <RocketOutlined />,
    },
];
